import React, { useState } from 'react';
import { Form } from 'react-final-form';

import {
  BadgeGroupAlert,
  Button,
  FFTextInput,
  GBTypography,
} from '@leapfinance/geebee-component-library';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { Box, LinearProgress } from '@mui/material';

import { trackForgotPassword } from '@/segment/eventTracker';
import { useForgotPasswordMutation } from '@/services/base/auth';
import { LoginStatus } from '@/types/segmentEvents';
import { FORGOT_PASSWORD_ERROR_ENUM } from '@/utils/loginUtils';

import SentryError from '../lib/Sentry';

type Props = {
  onBack?: () => void;
};

type LoginHereSuggestionProps = {
  onBack?: () => void;
};

const errorsToExcludeForSentry = [
  FORGOT_PASSWORD_ERROR_ENUM.USER_NOT_FOUND,
  FORGOT_PASSWORD_ERROR_ENUM.DOMAIN_ERROR,
];

const LoginHereSuggestion = ({ onBack }: LoginHereSuggestionProps) => {
  return (
    <GBTypography
      variant="link2Regular"
      className="text-primary-500 flex items-center justify-center cursor-pointer"
      onClick={() => {
        onBack?.();
      }}
    >
      <KeyboardBackspaceIcon className="cursor-pointer pr-1" />
      Back to Login
    </GBTypography>
  );
};

const ForgotPassword = ({ onBack }: Props) => {
  const [errorFound, setErrorFound] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>(``);
  const [resetPwdEmailSent, setResetPwdEmailSent] = useState<boolean>(false);
  const [forgotPasswordMutation, { isLoading }] = useForgotPasswordMutation();
  const handleFormSubmit = async (values: any) => {
    if (values?.username === ``) {
      setErrorFound(true);
      setErrorMsg(`Please enter a valid username`);
      return;
    }
    setErrorFound(false);
    setErrorMsg(``);

    const payload = {
      userName: values?.username,
      appUrl: `${window.location.origin}/resetpwd`,
    };

    try {
      await forgotPasswordMutation(payload).unwrap();
      setResetPwdEmailSent(true);
      trackForgotPassword(LoginStatus.SUCCESS);
    } catch (error: any) {
      trackForgotPassword(LoginStatus.FAILED);
      setResetPwdEmailSent(false);
      if (!errorsToExcludeForSentry.includes(error.data.message)) {
        new SentryError(`Forgot Password Failed`, error);
      }
      if (error.data.message) {
        setErrorFound(true);
        setErrorMsg(error.data.message);
      } else {
        setErrorFound(true);
        setErrorMsg(`Something went wrong, please try again`);
      }
    }
  };

  return (
    <>
      {resetPwdEmailSent ? (
        <div className="p-2 text-center my-4">
          <CheckCircleIcon style={{ fontSize: `5rem`, color: `blue` }} />
          <p className="text-sm m-2">
            Reset password link has been sent to your registered email id
          </p>
          <LoginHereSuggestion
            onBack={() => {
              setResetPwdEmailSent(false);
              onBack?.();
            }}
          />
        </div>
      ) : (
        <div className="pt-20 flex flex-col gap-6">
          <div className="flex items-center justify-center">
            <GBTypography variant="h2Semibold">Forgot Password?</GBTypography>
          </div>
          {errorFound ? (
            <div className="px-6">
              <BadgeGroupAlert
                variant="warning"
                size="mobile"
                title={errorMsg}
              />
            </div>
          ) : null}
          <Form
            onSubmit={handleFormSubmit}
            render={({ handleSubmit }) => (
              <form
                onSubmit={handleSubmit}
                noValidate
                className="px-6 flex gap-6 flex-col justify-evenly h-[90%]"
              >
                <FFTextInput
                  fieldProps={{
                    name: `username`,
                    validate: (value) => {
                      if (!value) {
                        return `Please enter Username`;
                      }
                    },
                  }}
                  componentProps={{
                    required: true,
                    placeholder: `abcd@company`,
                    label: `Please enter your Username`,
                    onKeyDown: (event) => {
                      if (event.code === `Enter`) {
                        handleSubmit();
                      }
                    },
                    className: `bg-white`,
                    size: `small`,
                  }}
                />
                <div className="flex items-center justify-center">
                  <Button
                    variant="primary"
                    size="large"
                    className="w-[300px]"
                    onClick={handleSubmit}
                  >
                    Request New Password
                  </Button>
                </div>
                <LoginHereSuggestion onBack={onBack} />
                {isLoading && (
                  <Box>
                    <LinearProgress color="primary" />
                  </Box>
                )}
              </form>
            )}
          />
        </div>
      )}
    </>
  );
};

export default ForgotPassword;
